<template>
  <div class="wrapper" v-if="!isLoading">
    <div class="container">
      <StrengthPageModal
        ref="strengthPageModal"
        :strengthData="strengthData"
        :currentDevelopPlan="currentDevelopPlan"
        :isSoloCompleted="isSoloCompleted"
        :subscriptionLevel="subscriptionLevel"
        :otherProgress="otherProgress"
      />
      <button class="btn btn-primary-back back-button" onclick="window.history.back();">
        Back
      </button>
      <div class="text-center">
        <header class="strength-header">
          <div class="image-container">
            <img
              class="strength-image"
              :src="strengthData.strength_image.url"
              :alt="`${strengthData.name} icon`"
              data-test="sp-image"
            />
          </div>
          <div class="header-text">
            <h1 class="strength-title" data-test="sp-name">{{ strengthData.name }}</h1>
            <hr class="hr-magazine hr-magazine-left hr-magazine-thick hr-magazine-short" />
            <div
              class="short-definition"
              v-html="cms.asHtml(strengthData.short_definition)"
              data-test="sp-short-def"
            ></div>
          </div>
        </header>
        <hr class="section splitter" />
        <div role="main">
          <section class="text-content" data-test="sp-long-description">
            <p
              v-for="(description, index) in strengthData.long_description"
              :key="index"
              class="description"
            >
              {{ description.text }}
            </p>
          </section>
          <hr class="section splitter" />
          <div class="button-container">
            <button
              v-if="currentDevelopPlan === strengthData.name"
              class="btn btn-primary develop-button"
              @click="openModal"
              data-test="sp-continue-button"
              type="button"
            >
              Continue
            </button>
            <button
              v-else
              class="btn btn-primary develop-button"
              @click="openModal"
              data-test="sp-develop-button"
              type="button"
            >
              Develop
            </button>
          </div>
          <div class="indicator-grid">
            <div class="grid-cell grid-header left-column pos-header">
              <img src="../../assets/positive-icon.svg" alt="positive" />
              <h2>Positive Indicators</h2>
            </div>
            <div class="grid-cell grid-header neg-header">
              <img src="../../assets/negative-icon.svg" alt="positive" />
              <h2>Negative Indicators</h2>
            </div>
            <div class="grid-cell left-column grid-list pos-desc">
              <ul data-test="sp-positives">
                <li v-for="(posIndicator, index) in strengthData.positive_indicators" :key="index">
                  {{ posIndicator.description[0].text }}
                </li>
              </ul>
            </div>
            <div class="grid-cell grid-list neg-desc">
              <ul data-test="sp-negatives">
                <li v-for="(negIndicator, index) in strengthData.negative_indicators" :key="index">
                  {{ negIndicator.description[0].text }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <LoadingPage v-else />
</template>

<script setup lang="ts">
import { onBeforeMount, ref, watchEffect } from "vue";
import { useRoute } from "vue-router";
import cms from "../../services/cms/cms";
import { CmsDocumentTypes } from "../../services/cms/cms-type-configuration";
import { CmsStrengthItemData } from "../../services/cms/cms-type-definitions";
import StrengthPageModal from "../../components/cms/strength-page-modal.vue";
import { csContext } from "../../services/cs-context";
import {
  DevelopStrengthProgressApiModel,
  LockStatusApiModel,
  ProfileApiModel,
  UserDetailsApiSubscriptionLevel,
} from "../../api-models";
import { useAxios } from "../../composables/api";
import LoadingPage from "../loading-page.vue";

const currentDevelopPlan = ref<DevelopStrengthProgressApiModel | undefined>(undefined);
const otherProgress = ref<DevelopStrengthProgressApiModel[] | undefined>(undefined);
const strengthData = ref<CmsStrengthItemData | null>(null);
const isSoloCompleted = ref<LockStatusApiModel | undefined>(
  csContext.userProfile.soloReportLockStatus,
);
const subscriptionLevel = ref<UserDetailsApiSubscriptionLevel | undefined>(
  csContext.user.subscriptionLevel,
);
const strengthPageModal = ref<InstanceType<typeof StrengthPageModal> | null>(null);
const strengthName = ref(useRoute().params.strength);

const isLoading = ref(true);

const openModal = (): void => {
  strengthPageModal.value?.openModal();
};

const getCmsData = async (): Promise<void> => {
  const response = await cms.getByUid(CmsDocumentTypes.CmsStrengthItem, `${strengthName.value}`);
  strengthData.value = response.data;
};

const { promise } = useAxios<ProfileApiModel>("/api/profile");
promise.then((profile) => {
  currentDevelopPlan.value = profile.developProgress?.currentStrengthProgress;
  otherProgress.value = profile.developProgress?.otherProgress;
});

watchEffect(() => {
  if (strengthData.value) {
    isLoading.value = false;
  }
});

onBeforeMount(async () => {
  await getCmsData();
});
</script>

<style scoped lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.wrapper {
  padding-top: 1rem;
  width: 100%;
  min-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

.container {
  width: 60%;
  @media (max-width: @screen-md) {
    width: 80%;
    margin-top: 4rem;
  }
  @media (max-width: 600px) {
    margin-top: 5rem;
    width: 90%;
  }

  padding-bottom: 40px;
}

.text-center {
  margin-top: 3rem;
  text-align: left;
  max-width: @screen-lg;
}

.strength-header {
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  gap: min(3rem);
  padding-left: 5rem;
  @media (max-width: @screen-sm) {
    flex-direction: column-reverse;
    gap: 0;
    padding: 0 2rem;
  }
}

.header-text {
  padding-top: 3rem;
  @media (max-width: @screen-xs) {
    padding-top: 0;
  }
}
.image-container {
  height: 200px;
  width: 200px;
  @media (max-width: @screen-sm) {
    height: 180px;
    width: 180px;
    margin: 0 auto;
  }
}
.strength-image {
  height: 200px;
  width: 200px;
  @media (max-width: @screen-sm) {
    height: 180px;
    width: 180px;
  }
}

.text-content {
  width: 100%;
  padding: 0 5rem;
  @media (max-width: @screen-sm) {
    padding: 0 2rem;
  }
}

.hr-magazine {
  border-color: @charismatic-color;
}

hr {
  margin-left: 0;
  margin-right: 0;
  color: @charismatic-color;
}

.section-splitter {
  width: 2px solid @dark-background-color;
}

h1 {
  @media (max-width: 450px) {
    font-size: 2.6rem;
  }
}

.button-container {
  width: 100%;
  display: flex;
  justify-content: center;
  margin: 3rem 0;
}

@80vw: 80%;
// used to calculate back-button margin on smaller screens
.back-button {
  position: absolute;
  margin-left: -130px;
  margin-top: 3rem;
  @media (max-width: @screen-md) {
    top: 5rem;
    //margin-left: calc(@80vw / 100 * 6);
    margin-top: 2rem;
    margin-left: 5rem;
  }
  @media (max-width: @screen-sm) {
    margin-left: 2rem;
  }
}

.indicator-grid {
  width: 100%;
  display: grid;
  grid-template-columns: 50% 50%;
  grid-template-rows: auto auto;

  @media (max-width: @screen-xs) {
    grid-template-columns: 100%;
    grid-template-rows: repeat(4, auto);
  }
}

.grid-cell {
  width: 100%;
}

.grid-header {
  border-bottom: 1px solid @dark-background-color;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 8rem;
  padding-left: 1rem;

  h2 {
    margin: 0;
    padding-left: 2rem;
    padding-right: 2rem;
    @media (max-width: @screen-md) {
      font-size: 1.5em;
    }
    @media (max-width: @screen-xs) {
      font-size: 1.2em;
    }
  }

  img {
    @media (max-width: @screen-lg) {
      padding-left: 2rem;
    }
  }
}

.pos-header {
  @media (max-width: @screen-xs) {
    grid-row: 1;
  }
}

.pos-desc {
  @media (max-width: @screen-xs) {
    grid-row: 2;
  }
}

.neg-header {
  @media (max-width: @screen-xs) {
    grid-row: 3;
  }
}

.neg-desc {
  @media (max-width: @screen-xs) {
    grid-row: 4;
  }
}

.left-column {
  border-right: 1px solid @dark-background-color;
  @media (max-width: @screen-xs) {
    border-right: none;
  }
}

.grid-list {
  padding: 3rem 3rem 1rem 0;

  li {
    padding-bottom: 1rem;
    margin-left: 2rem;
    font-size: 1.3rem;
    @media (max-width: @screen-md) {
      margin-left: 1rem;
    }
  }
}
</style>

<style lang="less">
// non-scoped styles for Prismic HTML
.short-definition {
  font-size: 2rem;
}
</style>
